import { Autocomplete, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useRegex } from "../../../core/hooks/useRegex";
import { useMascara } from "../../../core/hooks/useMascara";
import { useClientes } from "../../hooks/useClientes";
import { ICliente } from "../../interface/ICliente";
import { VincularClienteForm } from "./styles";
import { useNavigate } from "react-router-dom";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { AlertPermissaoFuncionalidade } from "../../../core/components/AlertPermissaoFuncionalidade";

interface VincularClienteProps {
  alterarModo: (modo: 'cadastrar' | 'vincular') => void;
  vincularCliente: (cliente: ICliente) => void;
  novoCliente: ICliente;
  handleNovoCliente: (cliente: ICliente) => void;
}

export const VincularCliente = ({ alterarModo, vincularCliente, novoCliente, handleNovoCliente }: VincularClienteProps) => {
  const { testeNumericoTelefone } = useRegex();
  const { mascaraTelefone } = useMascara();
  const { buscarClientesPorNomeOuTelefone } = useClientes();
  const [clientes, setClientes] = useState<ICliente[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<"nome" | "telefone">('nome');
  const [clienteSelecionado, setClienteSelecionado] = useState<ICliente | undefined>(undefined);
  const [filtro, setFiltro] = useState('');
  const [inputBusca, setInputBusca] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focar automaticamente no input quando o componente for montado
  useEffect(() => {
    const timer = setTimeout(() => {
      // Tentar diferentes seletores para encontrar o input
      let inputElement = document.querySelector('input[placeholder*="Nome ou telefone"]') as HTMLInputElement;
      
      if (!inputElement) {
        // Tentar por label
        inputElement = document.querySelector('input[aria-label*="Nome ou telefone"]') as HTMLInputElement;
      }
      
      if (!inputElement) {
        // Tentar por classe MUI
        inputElement = document.querySelector('.MuiAutocomplete-input') as HTMLInputElement;
      }
      
      if (!inputElement) {
        // Tentar por qualquer input dentro de um container de cliente
        const clienteContainer = document.querySelector('[data-testid*="cliente"], .cliente-container, [class*="cliente"]');
        if (clienteContainer) {
          inputElement = clienteContainer.querySelector('input') as HTMLInputElement;
        }
      }
      
      if (inputElement) {
        console.log('Focando no input de cliente encontrado:', inputElement);
        inputElement.focus();
      } else {
        console.log('Input não encontrado com nenhum seletor');
        // Log para debug - mostrar todos os inputs disponíveis
        const allInputs = document.querySelectorAll('input');
        console.log('Todos os inputs encontrados:', Array.from(allInputs).map(input => ({
          placeholder: input.getAttribute('placeholder'),
          ariaLabel: input.getAttribute('aria-label'),
          className: input.className
        })));
      }
    }, 500); // Delay ainda maior para garantir renderização completa
    
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (_event: React.SyntheticEvent<Element, Event>, valor: string, reason: string) => {
    if (reason === "reset") {
      setInputBusca(valor);
      return;
    }

    let valorTratado = valor;
    let novoFiltroTipo: 'nome' | 'telefone' = 'nome';

    if (testeNumericoTelefone(valor) && valor.length > 0) {
      valorTratado = mascaraTelefone(valor);
      novoFiltroTipo = 'telefone';
      novoCliente['telefone'] = valorTratado;
      novoCliente['nome'] = '';
    } else {
      novoCliente['nome'] = valorTratado;
      novoCliente['telefone'] = '';
    }

    setInputBusca(valorTratado);
    setFiltro(valorTratado.toLowerCase());
    setFiltroTipo(novoFiltroTipo);
    handleNovoCliente(novoCliente);

    if (!valorTratado.length) {
      setClientes([]);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      const clientesEncontrados = await buscarClientesPorNomeOuTelefone(valorTratado, novoFiltroTipo);
      const resultado = clientesEncontrados === null ? [] : clientesEncontrados;
      setClientes([...resultado, novoCliente]);
    }, 300);
  };

  const handleClienteChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ICliente | null
  ) => {
    if (value && value.uuid == 'novo') {
      return alterarModo('cadastrar');
    }

    if (!value) {
      return setClienteSelecionado(undefined);
    }

    return setClienteSelecionado(value);
  };

  const handleVincularCliente = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clienteSelecionado) {
      return;
    }

    vincularCliente(clienteSelecionado)
  }
  
  const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();

  return (
    <VincularClienteForm onSubmit={handleVincularCliente}>
      <Typography variant="h6">Cliente</Typography>
      {!perfil || !verificarAcessoFuncionalidade("Gestão de clientes") ? (
        <AlertPermissaoFuncionalidade funcionalidade="Gestão de clientes"  />
      ) : (
        <>
          <Autocomplete
            options={clientes}
            filterOptions={(x) => x}
            inputValue={inputBusca}
            onInputChange={handleInputChange}
            getOptionLabel={(cliente) => {
              if (filtroTipo == "telefone" && cliente["telefone"]) {
                return cliente["telefone"];
              }

              if (cliente['nome'].toLowerCase().includes(filtro)) {
                return cliente['nome'];
              }

              if (cliente['observacao'] && cliente['observacao'].toLowerCase().includes(filtro)) {
                return cliente['observacao'];
              }

              return cliente['nome'];
            }}
            onChange={handleClienteChange}
            style={{ width: "100%", margin: "1rem 0" }}
            noOptionsText={"Nenhum cliente encontrado"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nome ou telefone"
              />
            )}
            renderOption={(props, option) => {
              if (option.uuid == 'novo') {
                return (
                  <MenuItem {...props} key={option.uuid} value={option.uuid}>
                    <Button>Cadastrar {option.nome.length ? option.nome : option.telefone}</Button>
                  </MenuItem>
                );
              }

              if (option) {
                return (
                  <MenuItem {...props} key={option.uuid} value={option.uuid}>
                    {option.nome} {option.observacao ? ` ${option.observacao}` : ""} {option.telefone ? `- ${option.telefone}` : ''}
                  </MenuItem>
                );
              }

              return null;
            }}
          />
          <Button
            variant="contained"
            style={{ width: "100%" }}
            type="submit"
            disabled={!clienteSelecionado}
          >
            VINCULAR
          </Button>
        </>
      )}
    </VincularClienteForm>
  );
}